import axios from 'axios';
import { getConfig, getApiUrl } from '../config/appConfig';
import { broadcastLogout, clearAllAuthStorage, touchActivity } from '../utils/sessionManager';
import { getAuthApiKey, getAuthUser, hasAuthApiKey, candorWebRequestAuth, refreshCsrfFromCookie, getCsrfToken } from '../utils/authTokenStore';
import { resolveApiBaseUrl, pinApiBaseUrl, absoluteMatrixApiBaseUrl, getFailoverTarget, API_JSON_ACCEPT } from '../utils/apiBase';

// API Base URL: dev uses CRA proxy → http://127.0.0.1:8001 when base URL is empty.
const IS_DEV = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
function configuredApiBase(url = '') {
  try {
    if (typeof resolveApiBaseUrl === 'function') {
      return resolveApiBaseUrl(url);
    }
  } catch (_) {
    /* circular init / stale bundle */
  }
  const envBase = String(process.env.REACT_APP_API_BASE_URL || '').trim();
  if (envBase) return envBase.replace(/\/$/, '');
  return getConfig('api.baseUrl') || '';
}
const configuredBase = configuredApiBase();
// Fail closed: no inline host fallback — require REACT_APP_API_BASE_URL / host matrix / appConfig.
const finalUrl = IS_DEV
  ? configuredBase || ''
  : configuredBase || getConfig('api.baseUrl') || '';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: finalUrl,
  timeout: 60000, // Increased to 60 seconds for checkout session creation (address lookup may take time)
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: API_JSON_ACCEPT,
  },
});

// Set axios defaults to use configured URL
apiClient.defaults.baseURL = finalUrl;
apiClient.defaults.timeout = 60000; // Increased timeout for checkout operations

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Ensure baseURL tracks sticky pin after SPA misroute recovery or use case detection
    const liveBase = configuredApiBase(config.url);
    if (liveBase !== undefined && liveBase !== null) {
      config.baseURL = liveBase;
      apiClient.defaults.baseURL = liveBase;
    } else if (config.baseURL === undefined) {
      config.baseURL = finalUrl;
    }

    const auth = candorWebRequestAuth();
    config.withCredentials = true;
    Object.assign(config.headers, auth.headers);
    if (auth.headers['X-API-Key']) {
      config.headers['X-API-Key'] = auth.headers['X-API-Key'];
    } else {
      delete config.headers['X-API-Key'];
    }
    if (!config.headers.Accept) {
      config.headers.Accept = API_JSON_ACCEPT;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    if (hasAuthApiKey() && response.config?.url && !String(response.config.url).includes('/auth/login')) {
      touchActivity();
    }
    return response;
  },
  (error) => {
    // Handle connection errors gracefully
    if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
      // Don't reload the page for connection issues
      return Promise.reject({
        message: 'Connection timeout - server may be unavailable',
        code: 'CONNECTION_TIMEOUT',
        status: 0,
        data: null
      });
    }

    // ***** Code Start BH28082026_1115 - Axios one-shot retry on IIS/SPA misroute
    const status = error.response?.status;
    const ct = String(
      error.response?.headers?.['content-type'] ||
        error.response?.headers?.['Content-Type'] ||
        ''
    ).toLowerCase();
    const misroute = status === 406 || status === 405 || ct.includes('text/html');
    const isServerError = (status >= 502 && status <= 504) || (!error.response && error.code === 'ERR_NETWORK');
    if ((misroute || isServerError) && error.config && !error.config._apiBaseRetried) {
      try {
        const current = resolveApiBaseUrl();
        const alt = getFailoverTarget(current) || absoluteMatrixApiBaseUrl();
        if (alt && alt !== current) {
          pinApiBaseUrl(alt);
          apiClient.defaults.baseURL = alt;
          const retry = { ...error.config, _apiBaseRetried: true, baseURL: alt };
          return apiClient(retry);
        }
      } catch (_) {
        /* fall through */
      }
    }
    // ***** Code End BH28082026_1115

    /* Start Code - DC - 03082026 - Centralized Authorization & Token Header Injection */
    if (error.response?.status === 401) {
      const reqUrl = String(error.config?.url || '');
      const sessionProbe =
        reqUrl.includes('/users/me') || reqUrl.includes('/auth/sso/csrf');
      if (!sessionProbe && hasAuthApiKey() && !apiClient._unauthorizedLock) {
        apiClient._unauthorizedLock = true;
        broadcastLogout('unauthorized');
        clearAllAuthStorage();
        window.dispatchEvent(new CustomEvent('candor-unauthorized'));
      }
    }
    if (error.response?.status === 403 && !error.config?._csrfRetried) {
      const payload = error.response?.data;
      const blob = typeof payload === 'string' ? payload : JSON.stringify(payload || {});
      if (/csrf/i.test(blob)) {
        return refreshCsrfFromCookie().then((ok) => {
          if (!ok) return Promise.reject(error);
          const retry = { ...error.config, _csrfRetried: true };
          retry.headers = retry.headers || {};
          const csrf = getCsrfToken();
          if (csrf) retry.headers['X-CSRF-Token'] = csrf;
          return apiClient(retry);
        });
      }
    }
    /* End Code - DC - 03082026 - Centralized Authorization & Token Header Injection */
    return Promise.reject(error);
  }
);

// API Service class
class ApiService {
  constructor() {
    this.client = apiClient;
  }

  // Generic request method with retry logic
  async request(config, retryCount = 0) {
    // CRITICAL: Never retry payment/checkout requests — they are not idempotent.
    if (config.url && config.url.includes('/payments/')) {
      config.retry = 0;
    }

    try {
      const response = await this.client(config);

      // Check if response is HTML (404 page) instead of JSON
      if (typeof response.data === 'string' && response.data.includes('<html>')) {
        throw new Error('API endpoint not found - received HTML response');
      }

      return response.data;
    } catch (error) {
      // Handle connection timeout/aborted errors gracefully
      if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
        // Return a graceful fallback instead of throwing
        return {
          success: false,
          message: 'Server temporarily unavailable',
          fallback: true
        };
      }

      // Check if it's a timeout error
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        if (config.url && config.url.includes('/payments/')) {
          throw this.handleError(error);
        }
        if (retryCount < getConfig('api.retryAttempts')) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff, max 5s
          await this.delay(delay);
          return this.request(config, retryCount + 1);
        }
      } else if (retryCount < getConfig('api.retryAttempts') && !config.url?.includes('/payments/') && this.shouldRetry(error)) {
        await this.delay(1000 * (retryCount + 1)); // Exponential backoff
        return this.request(config, retryCount + 1);
      }

      throw this.handleError(error);
    }
  }

  shouldRetry(error) {
    return error.code === 'NETWORK_ERROR' ||
      error.code === 'ECONNABORTED' ||
      error.response?.status >= 500 ||
      error.response?.status === 429;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      let message = error.response.data?.message || error.response.data?.detail || getConfig('messages.errors.genericError');

      // Provide more specific error messages based on status
      if (status === 401) {
        message = 'Authentication failed. Please restart the application.';
      } else if (status === 403) {
        message = 'Access denied. Please check your permissions.';
      } else if (status === 404) {
        message = 'Requested resource not found.';
      } else if (status >= 500) {
        // ***** BH29072026_1745 — a 503 from the payment gateway carries the reason the
        // checkout was refused (missing Paddle permission / missing yearly price). The old
        // blanket "Service temporarily unavailable" hid it and looked like an outage.
        const serverDetail =
          typeof error.response.data?.detail === 'string'
            ? error.response.data.detail
            : null;
        message =
          serverDetail ||
          (status === 500
            ? 'Server error. Please try again later.'
            : 'Service temporarily unavailable. Please try again later.');
      }

      return {
        message,
        status,
        data: error.response.data,
      };
    } else if (error.request) {
      // Network error
      return {
        message: getConfig('messages.errors.networkError'),
        status: 0,
        data: null,
      };
    } else {
      // Other error (including timeout)
      return {
        message: error.message || getConfig('messages.errors.genericError'),
        status: 0,
        data: null,
      };
    }
  }

  // Authentication endpoints
  async exchangeAuthCode(authCode, context = {}) {
    const { exchangeIdeHandoffTicket } = await import('../utils/ideHandoff');
    const result = await exchangeIdeHandoffTicket(authCode, {
      source: context.source || 'subscription_dashboard',
      ...context,
    });
    if (!result.ok) {
      throw new Error(result.reason || 'Failed to establish secure session');
    }
    return {
      success: true,
      user: result.user,
      session_token: result.sessionToken,
      api_key: result.sessionToken,
      session_channel: result.cookieSession ? 'cookie' : undefined,
      token_type: result.cookieSession ? 'cookie' : undefined,
    };
  }

  async login(credentials) {
    return this.request({
      method: 'POST',
      url: getApiUrl('auth', '/login'),
      data: credentials,
    });
  }

  async logout() {
    return this.request({
      method: 'POST',
      url: getApiUrl('auth', '/logout'),
    });
  }

  async refreshToken() {
    return this.request({
      method: 'POST',
      url: getApiUrl('auth', '/refresh'),
    });
  }

  // User endpoints
  // ***** Code Start BH29072026_1105 - Env-driven endpoints (Sathis)
  async getUserProfile() {
    return this.request({
      method: 'GET',
      url: getConfig('api.endpoints.raw.USER_PROFILE', '/api/v1/users/profile'),
    });
  }

  async updateUserProfile(data) {
    return this.request({
      method: 'PUT',
      url: getConfig('api.endpoints.raw.USER_PROFILE', '/api/v1/users/profile'),
      data,
    });
  }

  // Subscription endpoints
  // AD -11072025 -start
  async getSubscriptionStatus(forceFresh = false) {
    try {
      const statusPath = getConfig('api.endpoints.raw.SUBSCRIPTION_STATUS', '/api/v1/subscriptions/status');
      // Add cache-busting headers to ensure fresh data from database
      const config = {
        method: 'GET',
        url: statusPath,
      };

      if (forceFresh) {
        // Add cache-busting headers and timestamp to ensure fresh fetch
        config.headers = {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        };
        // Add timestamp query parameter for cache busting
        config.url = `${statusPath}?t=${Date.now()}`;
      }

      return await this.request(config);
    } catch (error) {
      throw error;
    }
  }
  // AD -11072025 -end

  async updateSubscription(data) {
    return this.request({
      method: 'PUT',
      url: getConfig('api.endpoints.raw.SUBSCRIPTION_UPDATE', '/api/v1/subscriptions/update'),
      data,
    });
  }

  // Get all subscription plans from database
  async getSubscriptionPlans() {
    try {
      const response = await this.request({
        method: 'GET',
        url: getConfig('api.endpoints.raw.SUBSCRIPTION_PLANS', '/api/v1/subscriptions/plans'),
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get all add-on plans from database
  async getAddonPlans() {
    return this.request({
      method: 'GET',
      url: getConfig('api.endpoints.raw.SUBSCRIPTION_ADDONS', '/api/v1/subscriptions/plans/addons'),
    });
  }
  // ***** Code End BH29072026_1105

  async upgradeSubscription(planName) {
    return this.request({
      method: 'POST',
      url: getApiUrl('cdp', '/upgrade'),
      data: { plan_name: planName },
    });
  }

  async cancelSubscription() {
    // Cancel subscription may take longer due to Paddle API calls
    // Using /api/v1/payments/cancel-subscription which has complete Paddle integration
    return this.request({
      method: 'POST',
      url: getConfig('api.endpoints.raw.SUBSCRIPTION_CANCEL', '/api/v1/payments/cancel-subscription'),
      data: {},
      timeout: 60000  // 60 seconds for cancellation (Paddle API may be slow)
    });
  }

  async getUsageLimit() {
    return this.request({
      method: 'GET',
      url: getApiUrl('cdp', '/usage'),
    });
  }


  // Payment endpoints
  async getPaymentPlans() {
    return this.request({
      method: 'GET',
      url: getApiUrl('payments', '/plans'),
    });
  }

  async createCheckoutSession(planId, billingCycle = 'monthly', customerEmail = null) {
    const buildSuccessUrl = getConfig('paddle.buildSuccessUrl');
    const buildCancelUrl = getConfig('paddle.buildCancelUrl');

    let email = customerEmail;
    if (!email) {
      try {
        email = getAuthUser()?.email || null;
      } catch (_) {
        /* ignore */
      }
    }

    const payload = {
      plan_id: planId,
      success_url:
        typeof buildSuccessUrl === 'function'
          ? buildSuccessUrl(planId, billingCycle)
          : `${window.location.origin}/success?success=true&plan=${planId}&billing_cycle=${billingCycle}&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url:
        typeof buildCancelUrl === 'function'
          ? buildCancelUrl(planId, billingCycle)
          : `${window.location.origin}/success?success=false&plan=${planId}&billing_cycle=${billingCycle}`,
      billing_interval: billingCycle,
      billing_cycle: billingCycle,
    };
    if (email) {
      payload.customer_email = email;
    }

    return this.request({
      method: 'POST',
      url: getConfig('api.endpoints.raw.PAYMENT_CREATE_CHECKOUT', '/api/v1/payments/create-checkout-session'),
      data: payload,
    });
  }

  async getSessionStatus(sessionId) {
    return this.request({
      method: 'GET',
      url: getApiUrl('payments', `/session-status/${sessionId}`),
    });
  }

  /**
   * Idempotent checkout verification — called by SubscriptionContext after Stripe checkout success.
   * Applies credits (add-ons) or activates new subscription tier in the backend DB.
   * Safe to call multiple times; server uses stripe_webhook_events for deduplication.
   *
   * @param {{ sessionId: string, planId?: string }} params
   */
  async verifyCheckoutSession({ sessionId, planId, billingCycle = 'monthly' }) {
    return this.request({
      method: 'POST',
      url: getConfig('api.endpoints.raw.PAYMENT_VERIFY_CHECKOUT', '/api/v1/payments/verify-checkout-session'),
      data: {
        session_id: sessionId,
        plan_id: planId || null,
        billing_cycle: billingCycle,
        billing_interval: billingCycle,
      },
    });
  }

  /** Paddle txn_* verification (also used after overlay checkout.completed redirect). */
  async verifyPaddleTransaction({ transactionId, planId, billingCycle = 'monthly' }) {
    let email = null;
    try {
      email = getAuthUser()?.email || null;
    } catch (_) {
      /* ignore */
    }
    return this.request({
      method: 'POST',
      url: getConfig('api.endpoints.raw.PAYMENT_VERIFY_TRANSACTION', '/api/v1/payments/verify-transaction'),
      data: {
        transaction_id: transactionId,
        plan_id: planId || null,
        billing_cycle: billingCycle,
        email,
      },
    });
  }


  async getBillingHistory(forceFresh = false) {
    const billingPath = getConfig('api.endpoints.raw.PAYMENT_BILLING_HISTORY', '/api/v1/payments/billing-history');
    const config = {
      method: 'GET',
      url: billingPath,
    };

    if (forceFresh) {
      // Add cache-busting headers and timestamp to ensure fresh fetch
      config.headers = {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
      };
      // Add timestamp query parameter for cache busting
      config.url = `${billingPath}?t=${Date.now()}`;
    }

    return this.request(config);
  }

  /**
   * Get invoice URL for a Paddle transaction by calling Paddle API
   * Returns a temporary signed URL that expires after 1 hour
   */
  async getTransactionInvoice(transactionId) {
    return this.request({
      method: 'GET',
      url: `/api/v1/payments/transactions/${transactionId}/invoice`,
    });
  }
  // AD -11072025 -end

  async getPaddleInvoicesByEmail(email) {
    // Optional backend support; gracefully handle absence
    const query = email ? `?email=${encodeURIComponent(email)}` : '';
    try {
      return await this.request({
        method: 'GET',
        url: `${getConfig('api.endpoints.raw.PAYMENT_PADDLE_INVOICES', '/api/v1/payments/paddle/invoices')}${query}`,
      });
    } catch (e) {
      // Return empty on unsupported endpoint
      return [];
    }
  }

  // Legacy method name for backward compatibility
  async getStripeInvoicesByEmail(email) {
    return this.getPaddleInvoicesByEmail(email);
  }

  async updatePaymentMethod(paymentMethodId) {
    return this.request({
      method: 'POST',
      url: getApiUrl('payments', '/update-payment-method'),
      data: { payment_method_id: paymentMethodId },
    });
  }

  // Usage analytics endpoints
  // AD -11072025 -start
  async getUsageData(forceFresh = false) {
    const usagePath = getConfig('api.endpoints.raw.SUBSCRIPTION_USAGE_LIMIT', '/api/v1/subscriptions/usage-limit');
    const config = {
      method: 'GET',
      url: usagePath,
    };

    if (forceFresh) {
      // Add cache-busting headers and timestamp to ensure fresh fetch
      config.headers = {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
      };
      // Add timestamp query parameter for cache busting
      config.url = `${usagePath}?t=${Date.now()}`;
    }

    return this.request(config);
  }
  // AD -11072025 -end

  async getUsageAnalytics() {
    return this.request({
      method: 'GET',
      url: getApiUrl('users', '/analytics'),
    });
  }

  // Manual subscription update (for desktop app integration)
  async manualUpdateSubscription(data) {
    return this.request({
      method: 'POST',
      url: getApiUrl('payments', '/manual-update-subscription'),
      data,
    });
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export default apiService;
